import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useSystemQuestions } from '../hooks/useSystemQuestions';
import { useSystemQuestionMutations } from '../hooks/useSystemQuestionMutations';
import { QuestionList } from '../components/QuestionList';
import { CreateQuestionDialog } from '../components/CreateQuestionDialog';
import type {
  RefereeQuestion,
  CreateRefereeQuestionRequest,
} from '../types/referee-question.types';

export function SystemQuestionsSettingsPage(): React.JSX.Element {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<RefereeQuestion | null>(null);
  const { questions, hasData, isLoading, error, refetch } = useSystemQuestions();
  const { create, update, remove, isLoading: isMutating } = useSystemQuestionMutations();

  const handleCreate = (data: CreateRefereeQuestionRequest): void => {
    create(data, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
        refetch();
      },
    });
  };

  const handleUpdate = (data: { questionId: string; text: string; isRequired: boolean }): void => {
    update(data, {
      onSuccess: () => {
        setIsEditDialogOpen(false);
        setEditingQuestion(null);
        refetch();
      },
    });
  };

  const handleDelete = (questionId: string): void => {
    remove(questionId, {
      onSuccess: () => refetch(),
    });
  };

  const handleEdit = (question: RefereeQuestion): void => {
    setEditingQuestion(question);
    setIsEditDialogOpen(true);
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">System reference contact questions</h1>
          <p className="text-muted-foreground">
            Configure system-wide questions for all reference contacts
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>Add Question</Button>
      </div>

      <QuestionList
        questions={questions}
        hasData={hasData}
        isLoading={isLoading}
        error={error}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <CreateQuestionDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleCreate}
        isLoading={isMutating}
      />

      <CreateQuestionDialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) setEditingQuestion(null);
        }}
        onSubmit={handleCreate}
        onUpdate={handleUpdate}
        editingQuestion={editingQuestion}
        isLoading={isMutating}
      />
    </div>
  );
}
